import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, generatePath } from 'react-router-dom';

import {
  CategoryListItemExt,
  queryClient,
  useCategoriesListQuery,
  useCommentsListQuery,
  useDeletePostMutation,
  usePostDetails,
} from 'api';
import { alert } from 'components';
import { useAuth, useModal } from 'hooks';
import { Links } from 'settings';
import { InstanceType } from 'types';

export function usePostDetailsPage(postId: string, isTaskRoute = false) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isLoading, post, isFetched, isError } = usePostDetails({
    postId,
    queryName: 'post-details',
    include_reactions: true,
  });

  const isNotFound = isFetched && (isError || !post);

  useEffect(() => {
    if (isNotFound) {
      alert.error(t(isTaskRoute ? 'toasts.codes.taskWasDeleted' : 'toasts.codes.postWasDeleted'));
      navigate(Links.we.index);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotFound]);

  const { isOpen, handleCloseModal, handleOpenModal } = useModal();

  const { user, isManager } = useAuth();

  const {
    comments,
    isLoading: isLoadingComments,
    isFetchingNextPage,
    lastItemRef,
  } = useCommentsListQuery({
    limit: 20,
    postId: post?.id || '',
    type: InstanceType.tasks || InstanceType.polls,
  });
  const {
    categoriesList,
    isLoading: isCategoryListLoading,
  }: {
    categoriesList: CategoryListItemExt[];
    isLoading: boolean;
  } = useCategoriesListQuery();

  function navigateBack() {
    navigate(-1);
  }

  const isPoll = post?.type === 'polls';
  const isTask = post?.type === InstanceType.tasks;
  const isCurrentAuthor = Boolean(user?.id === post?.author.id);

  const canDeletePost = isManager || isCurrentAuthor;
  const canDeletePoll = isCurrentAuthor;
  const canDelete = isPoll ? canDeletePoll : canDeletePost;
  const canEdit = !isPoll && (isCurrentAuthor || isManager);

  const { deletePostMutation } = useDeletePostMutation(() => {
    queryClient.invalidateQueries({ queryKey: ['posts', 'post-details'] });
    navigate(-1);
  });

  async function handleDeletePost() {
    await deletePostMutation({ postId: post?.id || '' });
    handleCloseModal();
  }

  function handleOpenEditMode() {
    const postEditLink = generatePath(Links.posts.edit.postId, {
      documentId: post?.id ?? '',
    });
    const taskEditLink = generatePath(Links.we.taskManagement.edit.taskId, {
      taskId: post?.id ?? '',
    });
    navigate(isTask ? taskEditLink : postEditLink);
  }

  return {
    navigateBack,
    isLoading: isLoading || isCategoryListLoading || isNotFound,
    post,
    categoriesList,
    comments,
    isLoadingComments,
    isFetchingNextPage,
    lastItemRef,
    isPoll,
    isTask,
    canEdit,
    canDelete,
    isOpen,
    handleDeletePost,
    handleOpenModal,
    handleCloseModal,
    handleOpenEditMode,
  };
}
